import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { Image } from '../../components/Image'
import { ColumnType, makeStyledDateCell } from '../../components/Table'
import { Text } from '../../components/Text'
import { InvitedUser, hasAnyUnfilteredInvitedUsers } from '../../redux/account/profile-management'
import { DialogActions } from '../../redux/dialog'
import { DomainsActions, DomainType, getDomains } from '../../redux/domains'
import { RootState } from '../../redux/types'
import { createTFunction, getDomainDescription } from '../../utils/helpers'

import { ProfileManagementTable } from './ProfileManagementTable'
import { ProfileManagementTableHeader } from './ProfileManagementTableHeader'

const domainTypes = [DomainType.Role]

const chevronIcon = <Image alt="arrowRight" type="chevronRight" />

export type ProfileManagementDataProps = {
  invitedUsers: InvitedUser[] | undefined
}

export const ProfileManagementData = ({ invitedUsers }: ProfileManagementDataProps) => {
  const { t, i18n } = useTranslation(['account'])
  const tt = createTFunction('account:ProfileManagement.', t)

  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      DomainsActions.loadDomains({
        language: i18n.language,
        domainTypes,
        adminxCall: true
      })
    )
  }, [dispatch, i18n.language])

  const domains: ReturnType<typeof getDomains> = useSelector((state: RootState) =>
    getDomains(state, {
      domains: domainTypes,
      language: i18n.language
    })
  )

  const cellClickHandler = useCallback(
    (value: InvitedUser) => ({
      style: {
        cursor: 'pointer'
      },
      onClick: () => {
        dispatch(
          DialogActions.showDialogWithData({
            dialog: 'ProfileManagementEditAccount',
            data: {
              userId: value.id
            }
          })
        )
      }
    }),
    [dispatch]
  )

  const hasInvitedUsers = useSelector(hasAnyUnfilteredInvitedUsers)

  const tableColumns: ColumnType<InvitedUser>[] = useMemo(
    () =>
      hasInvitedUsers
        ? [
            {
              id: 'email',
              title: tt('Mail'),
              dataIndex: 'email',
              visibilityType: 'fixed',
              onCell: cellClickHandler,
              sorter: true,
              render: function renderEmailCol(value) {
                return (
                  <Text bold color="primary">
                    {value}
                  </Text>
                )
              }
            },
            {
              id: 'invitationDate',
              title: tt('InvitationDate'),
              dataIndex: 'invitationDate',
              sorter: true,
              render: makeStyledDateCell({ dateStyle: 'medium', timeStyle: undefined })
            },
            {
              id: 'activationDate',
              title: tt('ActivationDate'),
              dataIndex: 'activationDate',
              sorter: true,
              render: makeStyledDateCell({ dateStyle: 'medium', timeStyle: undefined })
            },
            {
              id: 'visibilityRoleName',
              title: tt('Visibility'),
              dataIndex: 'visibilityRoleName',
              sorter: true,
              render: (value: string) => getDomainDescription(DomainType.Role, value, domains)
            },
            {
              id: 'permissionRoleName',
              title: tt('Permission'),
              dataIndex: 'permissionRoleName',
              sorter: true,
              render: (value: string) => getDomainDescription(DomainType.Role, value, domains)
            },
            {
              id: 'officeName',
              title: tt('Office'),
              dataIndex: 'officeName',
              sorter: true
            },
            {
              id: 'arrow-icon',
              dataIndex: 'arrowIcon',
              width: '24px',
              visibilityType: 'non-selectable',
              onCell: cellClickHandler,
              render: () => chevronIcon
            }
          ]
        : [],
    [cellClickHandler, tt, domains, hasInvitedUsers]
  )

  return (
    <>
      {hasInvitedUsers && <ProfileManagementTableHeader tableColumns={tableColumns} />}
      <ProfileManagementTable tableColumns={tableColumns} tableData={invitedUsers} />
    </>
  )
}
